import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { ReactComponent as CopyLinkIcon } from 'assets/icons/share_link.svg';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type ShareWithTeamInviteModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  onNotify?: (message: string) => void;
  onCopyLink?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
};

type ShareWithTeamInviteModalContentProps = {
  onClose: () => void;
  onNotify: (message: string) => void;
  onCopyLink: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
};

const ShareWithTeamInviteModalContent = ({ onClose, onNotify, onCopyLink }: ShareWithTeamInviteModalContentProps) => {
  const { t } = useTranslation('common_navbar');

  const [showInviteBody, setShowInviteBody] = useState(true);
  const [message, setMessage] = useState('');

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = event?.target?.value;
    setMessage(inputValue);
  };

  const handleClick = () => {
    setShowInviteBody(false);
    onNotify(message);
  };

  const modalTestId = 'share-with-team-invite-modal';
  const doNotSendButtonTestId = 'share-with-team-invite-modal-donotsend-button';
  const sendButtonTestId = 'share-with-team-invite-modal-send-button';
  const copyLinkTestId = 'share-with-team-invite-modal-copylink-button';
  const messageTextAreaTestId = 'share-with-team-invite-modal-message-textarea';

  return (
    <ModalContent display={'flex'} bg='transparent' m='auto' data-testid={modalTestId}>
      {showInviteBody && (
        <ModalBody bg='white' borderRadius='md' mb='2' boxShadow='lg' color='font.500' p='0'>
          <ModalHeader color='font.500'>
            <Text fontSize={'md'} data-testid={messageTextAreaTestId}>
              {t('modals.share-panel.share-with-team.invite-title')}
            </Text>
          </ModalHeader>
          <ModalBody>
            <Flex flexDir='column' justifyContent='space-between' gap={4} pb={4} color='font.500'>
              <Textarea
                placeholder={t('modals.share-panel.share-with-team.invite-placeholder')}
                value={message}
                onChange={handleInputChange}
              />
            </Flex>
          </ModalBody>
          <ModalFooter gap='2'>
            <Button variant='outline' size='sm' onClick={onClose} data-testid={doNotSendButtonTestId}>
              {t('modals.share-panel.share-with-team.do-not-send')}
            </Button>
            <Button variant='action' size='sm' onClick={handleClick} data-testid={sendButtonTestId}>
              {t('modals.share-panel.share-with-team.send')}
            </Button>
          </ModalFooter>
        </ModalBody>
      )}

      <ModalBody bg='white' borderRadius='md' boxShadow='lg' p='0'>
        <ModalHeader color='font.500'>
          <Text fontSize={'md'}>{t('modals.share-panel.share-with-team.share-link-title')}</Text>
        </ModalHeader>
        <ModalBody>
          <Flex flexDir='column' justifyContent='space-between' gap={4} pb={4} color='font.500'>
            <Flex w='full' gap='2'>
              <Text display='flex' alignItems='center' fontSize='sm'>
                {t('modals.share-panel.share-with-team.share-link-description')}
              </Text>
              <Spacer />
              <Button
                onClick={onCopyLink}
                variant='action'
                size='sm'
                width='28'
                leftIcon={<CopyLinkIcon style={{ marginRight: 1 }} />}
                data-testid={copyLinkTestId}
              >
                {t('modals.share-panel.copy-link')}
              </Button>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalBody>
    </ModalContent>
  );
};

export const ShareWithTeamInviteModal = ({
  isOpen,
  onClose = () => {},
  onNotify = () => {},
  onCopyLink = () => {},
}: ShareWithTeamInviteModalProps) => {
  return (
    <div>
      <Modal isOpen={isOpen} onClose={onClose} size='md'>
        <ModalOverlay />
        <ShareWithTeamInviteModalContent onNotify={onNotify} onCopyLink={onCopyLink} onClose={onClose} />
      </Modal>
    </div>
  );
};
